import React, { useEffect, useMemo, useState } from "react";
import { API_BASE_URL, authFetch } from "../../config/api";

const readJson = async (response) => {
  const raw = await response.text();
  try {
    return JSON.parse(raw);
  } catch {
    throw new Error(response.ok ? "Unexpected server response" : `Request failed (${response.status})`);
  }
};

const CostAnalysisTemplates = () => {
  const [templates, setTemplates] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [editorOpen, setEditorOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [editorClient, setEditorClient] = useState("");
  const [editorRows, setEditorRows] = useState([]);
  const [editorSaving, setEditorSaving] = useState(false);

  const loadTemplates = async () => {
    setLoading(true);
    setError("");
    try {
      const response = await authFetch(`${API_BASE_URL}/api/accounting/cost-analysis/templates`, { credentials: "include" });
      const json = await readJson(response);
      if (!response.ok || !json.success) throw new Error(json.message || "Failed to load templates");
      setTemplates(json.data || []);
    } catch (err) {
      setError(err.message || "Failed to load templates");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadTemplates(); }, []);

  const filteredTemplates = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return templates;
    return templates.filter((template) => String(template.corporate_client || "").toLowerCase().includes(term));
  }, [search, templates]);

  const openEditor = (template = null) => {
    setEditingTemplate(template);
    setEditorClient(template?.corporate_client || "");
    setEditorRows(
      (template?.billing_charge_rows || []).map((row) => ({
        particulars: row.particulars || "",
        base_amount: row.base_amount ?? "",
        currency: row.currency || "PHP",
      }))
    );
    setEditorOpen(true);
  };

  const updateEditorRow = (index, field, value) => {
    setEditorRows((rows) => rows.map((row, rowIndex) => (rowIndex === index ? { ...row, [field]: value } : row)));
  };

  const saveEditor = async () => {
    if (!editorClient.trim()) return setError("Corporate client is required");
    setEditorSaving(true);
    setError("");
    try {
      const endpoint = editingTemplate
        ? `${API_BASE_URL}/api/accounting/cost-analysis/templates/${editingTemplate.id}`
        : `${API_BASE_URL}/api/accounting/cost-analysis/templates`;
      const response = await authFetch(endpoint, {
        method: editingTemplate ? "PUT" : "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          corporate_client: editorClient.trim(),
          billing_charge_rows: editorRows,
        }),
      });
      const json = await readJson(response);
      if (!response.ok || !json.success) throw new Error(json.message || "Failed to save template");
      setEditorOpen(false);
      await loadTemplates();
    } catch (err) {
      setError(err.message || "Failed to save template");
    } finally {
      setEditorSaving(false);
    }
  };

  const deleteTemplate = async (template) => {
    if (!window.confirm(`Delete the ${template.corporate_client} template?`)) return;
    setError("");
    try {
      const response = await authFetch(`${API_BASE_URL}/api/accounting/cost-analysis/templates/${template.id}`, {
        method: "DELETE",
        credentials: "include",
      });
      const json = await readJson(response);
      if (!response.ok || !json.success) throw new Error(json.message || "Failed to delete template");
      setTemplates((current) => current.filter((item) => item.id !== template.id));
    } catch (err) {
      setError(err.message || "Failed to delete template");
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 p-6 font-poppins lg:p-8">
      <div className="mx-auto max-w-7xl space-y-6">
        <div className="rounded-3xl border border-white bg-white p-6 shadow-sm">
          <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">Cost Analysis</p>
              <h1 className="mt-2 text-3xl font-bold text-slate-900">Cost Analysis Templates</h1>
              <p className="mt-2 text-sm text-slate-600">Default billing charges saved for each corporate client.</p>
            </div>
            <div className="flex w-full gap-3 md:w-auto">
              <input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Search corporate client..." className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm outline-none focus:border-blue-500 md:w-72" />
              <button type="button" onClick={() => openEditor()} className="rounded-xl bg-blue-700 px-4 py-3 text-sm font-semibold text-white hover:bg-blue-800"><i className="fa-solid fa-plus mr-2" />Add</button>
              <button type="button" onClick={loadTemplates} className="rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm font-semibold text-slate-700 hover:bg-slate-50"><i className="fa-solid fa-rotate-right mr-2" />Refresh</button>
            </div>
          </div>
        </div>

        {error ? <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">{error}</div> : null}
        <div className="rounded-3xl border border-white bg-white shadow-sm">
          <div className="flex items-center justify-between border-b border-slate-100 px-6 py-4">
            <h2 className="text-lg font-semibold text-slate-900">Saved Templates</h2>
            <span className="text-sm text-slate-500">{filteredTemplates.length} template{filteredTemplates.length === 1 ? "" : "s"}</span>
          </div>
          {loading ? <div className="px-6 py-12 text-center text-sm text-slate-500"><i className="fa-solid fa-spinner fa-spin mr-2" />Loading templates...</div> : filteredTemplates.length ? (
            <div className="grid gap-4 p-6 md:grid-cols-2">
              {filteredTemplates.map((template) => (
                <div key={template.id} className="rounded-2xl border border-slate-200 bg-slate-50 p-5">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <h3 className="text-lg font-bold text-slate-900">{template.corporate_client}</h3>
                      <p className="mt-1 text-xs text-slate-500">{(template.billing_charge_rows || []).length} default billing charge{(template.billing_charge_rows || []).length === 1 ? "" : "s"}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="rounded-full bg-emerald-100 px-3 py-1 text-xs font-semibold text-emerald-700">Active</span>
                      <button type="button" onClick={() => openEditor(template)} className="rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-100" title="Edit template"><i className="fa-solid fa-pen-to-square" /></button>
                      <button type="button" onClick={() => deleteTemplate(template)} className="rounded-lg border border-rose-200 bg-white px-2.5 py-1.5 text-xs font-semibold text-rose-600 hover:bg-rose-50" title="Delete template"><i className="fa-solid fa-trash" /></button>
                    </div>
                  </div>
                  <div className="mt-4 space-y-2">
                    {(template.billing_charge_rows || []).map((row, index) => (
                      <div key={row.id || index} className="flex items-center justify-between gap-3 rounded-xl bg-white px-3 py-2 text-sm">
                        <span className="min-w-0 truncate font-medium text-slate-700">{row.particulars || "Unnamed charge"}</span>
                        <span className="shrink-0 text-slate-500">{row.currency || "PHP"} {Number(row.base_amount || 0).toLocaleString("en-US", { minimumFractionDigits: 2 })}</span>
                      </div>
                    ))}
                    {!(template.billing_charge_rows || []).length ? <p className="text-sm text-slate-500">No billing charges configured.</p> : null}
                  </div>
                  <p className="mt-4 text-xs text-slate-400">Updated {template.updated_at ? new Date(template.updated_at).toLocaleString() : "—"}</p>
                </div>
              ))}
            </div>
          ) : <div className="px-6 py-12 text-center text-sm text-slate-500">No Cost Analysis templates found.</div>}
        </div>
      </div>
      {editorOpen ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/50 p-4">
          <div className="w-full max-w-2xl rounded-3xl bg-white p-6 shadow-2xl">
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">Cost Analysis</p>
                <h2 className="mt-1 text-2xl font-bold text-slate-900">{editingTemplate ? "Edit Template" : "Add Template"}</h2>
              </div>
              <button type="button" onClick={() => setEditorOpen(false)} className="rounded-full p-2 text-slate-500 hover:bg-slate-100" aria-label="Close"><i className="fa-solid fa-xmark" /></button>
            </div>
            <label className="mt-5 block text-sm font-semibold text-slate-700">Corporate client</label>
            <input value={editorClient} onChange={(event) => setEditorClient(event.target.value)} className="mt-2 w-full rounded-xl border border-slate-200 px-4 py-3 text-sm" />
            <div className="mt-5 flex items-center justify-between">
              <h3 className="font-semibold text-slate-900">Billing charges</h3>
              <button type="button" onClick={() => setEditorRows((rows) => [...rows, { particulars: "", base_amount: "", currency: "PHP" }])} className="rounded-lg border border-blue-200 px-3 py-2 text-xs font-semibold text-blue-700 hover:bg-blue-50"><i className="fa-solid fa-plus mr-1" />Add charge</button>
            </div>
            <div className="mt-3 max-h-72 space-y-2 overflow-y-auto">
              {editorRows.map((row, index) => (
                <div key={index} className="grid grid-cols-[1fr_8rem_5rem_auto] gap-2">
                  <input value={row.particulars} onChange={(event) => updateEditorRow(index, "particulars", event.target.value)} placeholder="Charge" className="rounded-xl border border-slate-200 px-3 py-2 text-sm" />
                  <input type="number" step="0.01" value={row.base_amount} onChange={(event) => updateEditorRow(index, "base_amount", event.target.value)} placeholder="Amount" className="rounded-xl border border-slate-200 px-3 py-2 text-sm" />
                  <input value={row.currency} onChange={(event) => updateEditorRow(index, "currency", event.target.value)} className="rounded-xl border border-slate-200 px-3 py-2 text-sm" />
                  <button type="button" onClick={() => setEditorRows((rows) => rows.filter((_, rowIndex) => rowIndex !== index))} className="rounded-xl border border-rose-200 px-3 text-rose-600 hover:bg-rose-50" title="Remove charge"><i className="fa-solid fa-trash" /></button>
                </div>
              ))}
              {!editorRows.length ? <p className="rounded-xl bg-slate-50 px-4 py-3 text-sm text-slate-500">No billing charges configured.</p> : null}
            </div>
            <div className="mt-6 flex justify-end gap-3">
              <button type="button" onClick={() => setEditorOpen(false)} className="rounded-xl border border-slate-200 px-4 py-3 text-sm font-semibold text-slate-700">Cancel</button>
              <button type="button" onClick={saveEditor} disabled={editorSaving} className="rounded-xl bg-blue-700 px-5 py-3 text-sm font-semibold text-white disabled:opacity-50">{editorSaving ? "Saving..." : "Save Template"}</button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default CostAnalysisTemplates;
